import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDiamond } from '@fortawesome/free-solid-svg-icons';
import { FiArrowRight, FiArrowUpRight, FiCopy, FiMapPin, FiUsers, FiX, FiChevronRight } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { getNetworkingRecommendations } from '../networkingApi';
import { safeExternalUrl } from './previewData';
import './networking.css';
import UpgradeDialog from '../upgrade/UpgradeDialog';
import { useUpgradeExperience } from '../upgrade/UpgradeContext';

const LOADING_STAGES = {
  searching: 'Finding people for your goals…',
  reviewing: 'Choosing relevant people and preparing your introductions…',
  finishing: 'Finishing your recommendations…',
};

export function NetworkAvatar({ person }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [person.photoUrl]);
  return <span className="opp-avatar">{safeExternalUrl(person.photoUrl) && !failed
    ? <img src={person.photoUrl} alt={person.name} referrerPolicy="no-referrer" onError={() => setFailed(true)} />
    : <span aria-hidden="true">{person.name.split(' ').slice(0, 2).map((part) => part[0]).join('')}</span>}</span>;
}

function NetworkTags({ tags = [], compact = false }) {
  return <span className="opp-tags">{tags.slice(0, compact ? 2 : 8).map((tag) => <span key={tag.id} title={tag.description}>{tag.label}</span>)}</span>;
}

function NetworkPerson({ person, onSelect }) {
  return <button type="button" className="opp-person" onClick={() => onSelect(person)} aria-label={`See why you should connect with ${person.name}`}>
    <NetworkAvatar person={person} /><span className="opp-person-copy"><strong>{person.name}</strong><span>{person.title} at {person.company}</span><small>{person.location}</small><NetworkTags tags={person.tags} compact /></span><span className="opp-connect-button">Connect</span>
  </button>;
}

function NetworkDialog({ title, onClose, children, labelId }) {
  const dialog = useRef(null);
  useEffect(() => {
    const returnFocus = document.activeElement;
    dialog.current?.showModal();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = overflow; if (returnFocus?.isConnected) returnFocus.focus(); };
  }, []);
  return createPortal(<dialog ref={dialog} className="opp-dialog opp-network-dialog" aria-labelledby={labelId}
    onCancel={(event) => { event.preventDefault(); onClose(); }}
    onClick={(event) => { if (event.target === dialog.current) { const rect = dialog.current.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) onClose(); } }}>
    <header><h2 id={labelId}>{title}</h2><button className="opp-icon-button" type="button" onClick={onClose} aria-label="Close connection details"><FiX /></button></header>
    {children}
  </dialog>, document.body);
}

export function NetworkBrief({ person, onClose }) {
  const [copied, setCopied] = useState('');
  const linkedin = safeExternalUrl(person.linkedinUrl);
  return <NetworkDialog title="Connection details" labelId="network-brief-title" onClose={onClose}>
    <div className="opp-network-profile"><NetworkAvatar person={person} /><div><h3>{person.name}</h3><p>{person.title} at {person.company}</p><small><FiMapPin />{person.location}</small></div></div>
    <NetworkTags tags={person.tags} />
    <section className="opp-network-reason"><h3>Why this connection matters</h3><p>{person.why}</p>
      <ul>{(person.tags || []).slice(0, 3).map((tag) => <li key={tag.id}><strong>{tag.label}</strong><span>{tag.evidence}</span></li>)}</ul>
    </section>
    <section className="opp-network-opening"><h3>Your opening message</h3><p className="opp-network-note">{person.openingMessage}</p>
      <div className="opp-actions"><button className="opp-button" type="button" onClick={async () => { try { await navigator.clipboard.writeText(person.openingMessage); setCopied('Message copied'); } catch { setCopied('Select the message and copy it manually.'); } }}><FiCopy />{copied === 'Message copied' ? 'Copied' : 'Copy message'}</button>
        {linkedin && <a className="opp-button is-secondary" href={linkedin} target="_blank" rel="noopener noreferrer">Open LinkedIn <FiArrowUpRight /></a>}</div>
      <p className="opp-network-caption" role="status">{copied || 'Add this note to your connection request.'}</p>
    </section>
  </NetworkDialog>;
}

export default function NetworkingSuggestions({ trial, user }) {
  const experience = useUpgradeExperience();
  const [scoutUpgrade, setScoutUpgrade] = useState(false);
  const [data, setData] = useState({ status: 'idle', people: [] });
  const [loading, setLoading] = useState(Boolean(user));
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [selected, setSelected] = useState(null);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    if (!user) return undefined;
    const controller = new AbortController();
    let timer;
    let recoveries = 0;
    const started = Date.now();
    setLoading(true); setError(''); setSelected(null); setShowAll(false);
    const load = async (prepare) => {
      try {
        const result = await getNetworkingRecommendations({ user, prepare, signal: controller.signal });
        if (controller.signal.aborted) return;
        setData(result);
        if ((result.recoverable || result.status === 'idle') && recoveries < 2) {
          recoveries += 1;
          setLoading(true);
          timer = setTimeout(() => load(true), 1000);
          return;
        }
        const running = ['queued', 'running'].includes(result.status);
        setLoading(running);
        if (running && Date.now() - started < 600000) timer = setTimeout(() => load(false), 8000);
        else if (running) { setLoading(false); setError('This search is taking too long. Try again to check its progress or restart an interrupted search.'); }
      } catch (e) { if (!controller.signal.aborted) { setLoading(false); setError(e.message); } }
    };
    void load(true);
    return () => { controller.abort(); clearTimeout(timer); };
  }, [user, attempt]);
  const people = data.people || [];
  const refreshing = loading && people.length > 0;
  const loadingMessage = LOADING_STAGES[data.stage] || LOADING_STAGES.searching;
  return <section className="opp-panel opp-network" aria-labelledby="opp-network-title" aria-busy={loading}>
    <header className="opp-panel-heading"><h2 id="opp-network-title"><FiUsers />People you should connect with {trial && <FontAwesomeIcon icon={faDiamond} className="opp-diamond" title="Included in your trial" />}</h2>
      {people.length > 0 && <div className="opp-network-links"><button type="button" className="opp-text-link" onClick={() => setShowAll(true)}>View all <FiArrowRight /></button></div>}</header>
    {loading && !people.length ? <div className="opp-network-loading" role="status"><p>{loadingMessage}</p>{[0, 1, 2].map((n) => <div className="opp-network-placeholder" key={n} aria-hidden="true"><i /><div><i /><i /><i /></div></div>)}</div>
      : <div className="opp-people-list">{people.slice(0, 3).map((person) => <NetworkPerson key={person.id} person={person} onSelect={setSelected} />)}</div>}
    {!people.length && !loading && !error && data.status !== 'error' && <div className="opp-network-empty">
      {data.status === 'profile_required' ? <><p>Add your target roles and locations to find relevant people.</p><Link className="opp-text-link" to="/configure-scout" onClick={(event) => { if (!experience?.paid) { event.preventDefault(); setScoutUpgrade(true); } }}>Update Job Scout <FiArrowRight /></Link></>
        : <p>{data.status === 'locked' ? 'Personal networking recommendations are included with Pro.' : 'No suitable new people found yet. We’ll keep looking for your next update.'}</p>}
    </div>}
    {!loading && (error || data.status === 'error') && <div className="opp-network-error" role="alert"><p>{error || data.message || 'Couldn’t finish the search. Please try again.'}</p><button className="opp-button is-secondary" type="button" onClick={() => setAttempt((n) => n + 1)}>Try again</button></div>}
    {people.length > 0 && <p className="opp-network-cadence" role="status">{refreshing ? loadingMessage : trial ? 'Your trial recommendations' : data.nextRefreshAt ? `Next update ${new Intl.DateTimeFormat('en', { weekday: 'short', month: 'short', day: 'numeric' }).format(data.nextRefreshAt)}` : 'New people twice a week'}</p>}
    {showAll && <NetworkDialog title="People you should connect with" labelId="network-all-title" onClose={() => setShowAll(false)}><div className="opp-network-all">{people.map((person) => <button type="button" key={person.id} onClick={() => { setShowAll(false); setSelected(person); }}><NetworkAvatar person={person} /><span><strong>{person.name}</strong><small>{person.title} at {person.company}</small><NetworkTags tags={person.tags} compact /></span><FiChevronRight /></button>)}</div></NetworkDialog>}
    {scoutUpgrade && !experience?.paid && <UpgradeDialog user={user} context="scout_edit" onClose={() => setScoutUpgrade(false)} destination={{ path: "/configure-scout" }} />}
    {selected && <NetworkBrief key={selected.id} person={selected} onClose={() => setSelected(null)} />}
  </section>;
}
